import { createColumnHelper } from '@tanstack/react-table'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { usePreferences } from 'context/preferences/PreferencesContext'
import type { WorkflowStatus } from 'models/campaign'
import { ContactResponse } from 'models/contacts'
import { useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { formatDateWithTimeAndMinutes } from 'utils/formatting'
import { displayedSequenceStatus, SEQUENCE_COLUMNS } from './sequenceColumnInfo'

// DS2026 badge geometry — pill (16px radius), 14px Medium with -0.2px tracking.
// Colors go through the contrast-checked `ds2026-badge-*` tokens (subtle bg +
// colored text) rather than raw Tailwind palette, so each status keeps its
// distinct hue while satisfying the design-token rule (green→success,
// amber→warning, red→error; indigo→violet has no 1:1 token).
const CHIP_BASE =
  'inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]'

const STATUS_CONFIG: Record<
  string,
  { label: string; className: string; tooltip?: string }
> = {
  in_sequence: {
    label: 'Active',
    className: 'bg-ds2026-badge-blue-bg text-ds2026-badge-blue-text',
  },
  sequence_finished: {
    label: 'Completed',
    className: 'bg-ds2026-badge-success-bg text-ds2026-badge-success-text',
  },
  paused: {
    label: 'Paused',
    className: 'bg-ds2026-badge-warning-bg text-ds2026-badge-warning-text',
  },
  unsubscribed: {
    label: 'Unsubscribed',
    className: 'bg-ds2026-badge-orange-bg text-ds2026-badge-orange-text',
  },
  bounced: {
    label: 'Bounced',
    className: 'bg-ds2026-badge-error-bg text-ds2026-badge-error-text',
    tooltip: 'Email bounced. Find a new address for this contact.',
  },
  queued: {
    label: 'Queued',
    className: 'bg-ds2026-badge-violet-bg text-ds2026-badge-violet-text',
  },
  never_contacted: {
    label: 'Never Contacted',
    className: 'bg-ds2026-badge-gray-bg text-ds2026-badge-gray-text',
  },
  replied: {
    label: 'Replied',
    className: 'bg-ds2026-badge-purple-bg text-ds2026-badge-purple-text',
  },
  removed: {
    label: 'Removed',
    className: 'bg-ds2026-badge-rose-bg text-ds2026-badge-rose-text',
  },
  awaiting_launch: {
    label: 'Awaiting launch',
    className: 'bg-ds2026-badge-gray-bg text-ds2026-badge-gray-text',
    tooltip: 'Added to the sequence when the campaign launches.',
  },
}

const NOT_IN_SEQUENCE_CONFIG = {
  label: 'Not in Sequence',
  className: 'bg-ds2026-badge-warning-bg text-ds2026-badge-warning-text',
}

function SequenceStatusChip({ status }: { status: string | null | undefined }) {
  const config: { label: string; className: string; tooltip?: string } = status
    ? STATUS_CONFIG[status] || {
        label: status,
        className: 'bg-ds2026-badge-gray-bg text-ds2026-badge-gray-text',
      }
    : NOT_IN_SEQUENCE_CONFIG

  const chip = (
    <span className={`${CHIP_BASE} ${config.className}`}>{config.label}</span>
  )
  if (!config.tooltip) return chip
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{config.tooltip}</Tooltip>}
    >
      {chip}
    </OverlayTrigger>
  )
}

function NumericCell({ value }: { value: number | null | undefined }) {
  if (value == null)
    return <span className="text-fg-secondary">{'\u2013'}</span>
  return <span className="tabular-nums">{value}</span>
}

export function useSequenceColumns({
  campaignWorkflowStatus,
}: {
  campaignWorkflowStatus?: WorkflowStatus
} = {}) {
  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<ContactResponse>()
  const seqColumnHelper = createTableColumnHelper({
    columns: SEQUENCE_COLUMNS,
    tooltips,
  })

  return useMemo(
    () => [
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_membership_status') as any),
        id: 'seq_membership_status',
        size: 160,
        cell: (info: any) => {
          const row = info.row.original
          return (
            <SequenceStatusChip
              status={displayedSequenceStatus(
                row.seq_membership_status,
                campaignWorkflowStatus
              )}
            />
          )
        },
      }),
      columnHelper.accessor('seq_scheduled_emails', {
        ...(seqColumnHelper.retrieveColumn('seq_scheduled_emails') as any),
        size: 150,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_emails_sent', {
        ...(seqColumnHelper.retrieveColumn('seq_emails_sent') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_replies', {
        ...(seqColumnHelper.retrieveColumn('seq_replies') as any),
        size: 120,
        cell: (info: any) => <NumericCell value={info.getValue()} />,
      }),
      columnHelper.accessor('seq_last_email', {
        ...(seqColumnHelper.retrieveColumn('seq_last_email') as any),
        size: 200,
        cell: (info: any) => {
          const value = info.getValue()
          if (!value)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm tabular-nums">
              {formatDateWithTimeAndMinutes(new Date(value))}
            </span>
          )
        },
      }),
      columnHelper.display({
        ...(seqColumnHelper.retrieveColumn('seq_sequence_step') as any),
        id: 'seq_sequence_step',
        size: 150,
        cell: (info: any) => {
          const row = info.row.original
          if (row.seq_steps_completed == null || row.seq_total_steps == null)
            return <span className="text-fg-secondary">{'\u2013'}</span>
          return (
            <span className="text-sm">
              <span className="font-medium tabular-nums">
                {row.seq_steps_completed}
              </span>
              <span className="text-fg-secondary">
                {' '}
                of {row.seq_total_steps}
              </span>
            </span>
          )
        },
      }),
    ],
    [preferences, tooltips, campaignWorkflowStatus]
  )
}
